import React, { useState } from "react";
import { X, Save } from "lucide-react";
import { IMAGE_FIELDS, IMAGE_RECOMMENDATIONS } from "@/pages/admin/adminImageMeta";
import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";

// Generic CRUD drawer used for the plain resources (software, brands,
// testimonials) that don't need a dedicated rich editor.
export const EditDrawer = ({ item, fields, resourceKey, onClose, onSave }) => {
  const [form, setForm] = useState(item || {});
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-end" onClick={onClose}>
      <aside className="bg-[#0F0F0F] w-full max-w-lg h-full border-l border-[#262626] p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl font-bold">{item.id ? "Edit" : "Create"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          {fields.map((f) => {
            const isImage = IMAGE_FIELDS.has(f);
            const isBoolean = f === "featured" || f === "active";
            const isRowSelect = f === "row";
            // Sensible defaults when the record predates a new field
            // (legacy Brands rows created before the two-row marquee
            // had no `row`/`active`). Show them as Row 1 / Active so
            // the drawer never appears as if the field is blank.
            const defaultForField =
              f === "row" ? 1
              : f === "active" ? true
              : isBoolean ? false
              : "";
            const value = form[f] ?? defaultForField;
            const recommended = IMAGE_RECOMMENDATIONS[`${resourceKey}.${f}`];
            return (
              <div key={f}>
                {isImage ? (
                  <ImageUploadField
                    label={f}
                    value={value}
                    onChange={(v) => setForm({ ...form, [f]: v })}
                    testId={`upload-${f}`}
                    recommended={recommended}
                  />
                ) : isRowSelect ? (
                  <>
                    <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Row</label>
                    <select
                      data-testid={`field-${f}`}
                      value={Number(value) || 1}
                      onChange={(e) => setForm({ ...form, [f]: Number(e.target.value) })}
                      className="mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
                    >
                      <option value={1}>Row 1 — scrolls right-to-left</option>
                      <option value={2}>Row 2 — scrolls left-to-right</option>
                    </select>
                    <div className="text-xs text-[#A3A3A3] mt-1">
                      Which of the two homepage marquee rows this logo belongs to.
                    </div>
                  </>
                ) : isBoolean ? (
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`field-${f}`}
                      checked={!!value}
                      onChange={(e) => setForm({ ...form, [f]: e.target.checked })}
                      className="w-5 h-5 mt-0.5 accent-[#FF6600] shrink-0"
                    />
                    <span>
                      <span className="text-sm font-semibold capitalize">{f}</span>
                      {f === "featured" && (
                        <span className="block text-xs text-[#A3A3A3] mt-1">
                          Show this service in the homepage "Creative Services That Grow Your Business" row.
                        </span>
                      )}
                      {f === "active" && (
                        <span className="block text-xs text-[#A3A3A3] mt-1">
                          Uncheck to hide this logo from the homepage marquee without deleting it.
                        </span>
                      )}
                    </span>
                  </label>
                ) : (
                  <>
                    <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{f}</label>
                    <input
                      data-testid={`field-${f}`}
                      value={value}
                      onChange={(e) => setForm({ ...form, [f]: f === "price" || f === "display_order" ? Number(e.target.value) : e.target.value })}
                      className="mt-2 w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <button data-testid="admin-save" onClick={() => onSave(form)} className="ur-btn-primary w-full mt-8">
          Save <Save className="w-4 h-4" />
        </button>
      </aside>
    </div>
  );
};
